import React from 'react';
import { 
  FaStar,
  FaBook,
  FaChalkboardTeacher,
  FaFutbol,
  FaPalette,
  FaLaptop,
  FaLeaf,
  FaCheckCircle,
} from 'react-icons/fa';

const WhyChooseUs = () => {
  const features = [
    {
      id: 1,
      icon: FaBook,
      iconGradient: "from-blue-500 to-blue-600",
      bgGradient: "from-blue-50 to-blue-100",
      title: "Holistic Curriculum",
      description: "CBSE affiliated curriculum focusing on all-round development from Pre to Class X",
      stats: { value: "40+ Subjects", label: "Available" },
      points: [
        { text: "Comprehensive syllabus", color: "text-blue-500" },
        { text: "Value-based education", color: "text-blue-500" },
        { text: "Life skills training", color: "text-blue-500" }
      ],
      barColor: "from-blue-500 to-blue-600"
    },
    {
      id: 2,
      icon: FaChalkboardTeacher,
      iconGradient: "from-purple-500 to-purple-600",
      bgGradient: "from-purple-50 to-purple-100",
      title: "Expert Faculty",
      description: "Highly qualified teachers with 15+ years average experience and continuous training",
      stats: { value: "50+ Teachers", label: "Available" },
      points: [
        { text: "Specialized subject experts", color: "text-purple-500" },
        { text: "Regular professional development", color: "text-purple-500" },
        { text: "Individual attention focus", color: "text-purple-500" }
      ],
      barColor: "from-purple-500 to-purple-600"
    },
    {
      id: 3,
      icon: FaFutbol,
      iconGradient: "from-emerald-500 to-emerald-600",
      bgGradient: "from-emerald-50 to-emerald-100",
      title: "Sports Excellence",
      description: "Comprehensive sports program with professional coaching and modern facilities",
      stats: { value: "15+ Sports", label: "Available" },
      points: [
        { text: "Annual sports meets", color: "text-emerald-500" },
        { text: "Professional coaches", color: "text-emerald-500" },
        { text: "District level winners", color: "text-emerald-500" }
      ],
      barColor: "from-emerald-500 to-emerald-600"
    },
    {
      id: 4,
      icon: FaPalette,
      iconGradient: "from-amber-500 to-amber-600",
      bgGradient: "from-amber-50 to-amber-100",
      title: "Creative Arts",
      description: "Vibrant arts program nurturing creativity through diverse artistic expressions",
      stats: { value: "20+ Arts", label: "Available" },
      points: [
        { text: "Art and craft studio", color: "text-amber-500" },
        { text: "Music and dance rooms", color: "text-amber-500" },
        { text: "Annual cultural fest", color: "text-amber-500" }
      ],
      barColor: "from-amber-500 to-amber-600"
    },
    {
      id: 5,
      icon: FaLaptop,
      iconGradient: "from-indigo-500 to-indigo-600",
      bgGradient: "from-indigo-50 to-indigo-100",
      title: "Smart Classes",
      description: "Technology-enabled classrooms with interactive digital learning tools and resources",
      stats: { value: "Classrooms", label: "Available" },
      points: [
        { text: "Digital smart boards", color: "text-indigo-500" },
        { text: "Computer labs", color: "text-indigo-500" },
        { text: "E-library access", color: "text-indigo-500" }
      ],
      barColor: "from-indigo-500 to-indigo-600"
    },
    {
      id: 6,
      icon: FaLeaf,
      iconGradient: "from-green-500 to-green-600",
      bgGradient: "from-green-50 to-green-100",
      title: "Green Campus",
      description: "Sustainable 5-acre campus promoting environmental awareness and eco-friendly practices",
      stats: { value: "Large Campus", label: "Available" },
      points: [
        { text: "Solar powered facilities", color: "text-green-500" },
        { text: "Organic gardens", color: "text-green-500" },
        { text: "Waste management system", color: "text-green-500" }
      ],
      barColor: "from-green-500 to-green-600"
    }
  ];

  return (
    <section className="py-12 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 text-sm font-medium text-blue-600 rounded-full bg-blue-50">
            <FaStar className="w-4 h-4" />
            <span>Why Choose Gyan Rashmi</span>
          </div>
          <h2 className="mb-4 text-3xl font-bold text-gray-900">
            Excellence in{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Modern Education
            </span>
          </h2>
          <p className="text-gray-600">
            A comprehensive learning environment that nurtures academic excellence, creativity, and personal growth
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.id}
              className={`group relative overflow-hidden rounded-xl border border-gray-100 bg-gradient-to-br ${feature.bgGradient} transition-all duration-300 hover:shadow-xl hover:-translate-y-2`}
            >
              <div className="p-6">
                {/* Header with Icon and Stats */}
                <div className="flex items-start justify-between mb-5">
                  <div className={`p-3 rounded-xl text-white shadow-sm bg-gradient-to-r ${feature.iconGradient}`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{feature.stats.value}</div>
                    <div className="text-xs text-gray-500">{feature.stats.label}</div>
                  </div>
                </div>

                {/* Title */}
                <h3 className="mb-3 text-lg font-bold text-gray-900">{feature.title}</h3>

                {/* Description */}
                <p className="mb-5 text-sm leading-relaxed text-gray-600">{feature.description}</p>

                {/* Points List */}
                <div className="space-y-2">
                  {feature.points.map((point, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <FaCheckCircle className={`w-4 h-4 mt-0.5 ${point.color}`} />
                      <span className="text-sm text-gray-700">{point.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Bar - appears on hover */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.barColor} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
              
              {/* Top Right Decorative Element */}
              <div className={`absolute top-0 right-0 w-16 h-16 ${feature.barColor.replace('from-', 'bg-').replace('to-', '/5')} rounded-bl-3xl opacity-5`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;